import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { ShareContextModalLoadable } from '../share-context-modal/ShareContextModalLoadable.tsx';
import { ShareContextAgentIcons } from '../ShareContextAgentIcons.tsx';

// Button has fixed vertical padding, so the footer uses Pressable to match the design.
const styles = cssMap({
	footerButton: {
		display: 'flex',
		height: '32px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'stretch',
		gap: token('space.100'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	fullWidth: {
		width: '100%',
	},
});

const i18n = defineMessages({
	shareContextWithAgent: {
		id: 'byline-connections.preview-panel.footer.share-context-with-agent',
		defaultMessage: 'Share context with AI agent',
		description:
			'Button label in the Teamwork Graph panel footer. Opens options to share context with an artificial intelligence agent.',
	},
	shareContext: {
		id: 'byline-connections.preview-panel.modal.share-context-button',
		defaultMessage: 'Share context',
		description:
			"Label for the button in the Teamwork Graph modal's header that shares this content's Teamwork Graph context — the connected work items, documents and people — with someone else.",
	},
});

export type TeamworkGraphShareContextButtonProps = {
	contentId?: string;
	testId?: string;
	/** Stretches the button to fill its slot, for callers laying it out in a flexible row. */
	shouldFitContainer?: boolean;
	variant?: 'default' | 'footer';
	/**
	 * Hands modal ownership to the caller: the click still reports analytics, but this component
	 * renders no modal and calls `onOpen` instead of opening one itself. Required inside the byline
	 * popup, where a click in the modal's portal reads as an outside click and unmounts the popup —
	 * taking the button and any modal it owned with it. Omit it to keep the self-contained
	 * behaviour the panel footer and the Teamwork Graph modal rely on.
	 */
	onOpen?: () => void;
	/** Overrides the event source so a caller's surface is distinguishable in analytics. */
	source?: string;
	/** Forwarded to the modal's Rovo button. Unused with `onOpen`, where the caller owns the modal. */
	onAskRovo?: (prompt: string) => void;
};

export function TeamworkGraphShareContextButton({
	contentId,
	testId = 'teamwork-graph-modal-share-context',
	shouldFitContainer = false,
	variant = 'default',
	onOpen,
	source = ANALYTICS_SOURCE,
	onAskRovo,
}: TeamworkGraphShareContextButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hasOpenedModal, setHasOpenedModal] = useState(false);

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: SHARE_CONTEXT_ACTION_SUBJECT_ID,
				source,
			},
		}).fire();
		if (onOpen) {
			onOpen();
			return;
		}
		setHasOpenedModal(true);
		setIsModalOpen(true);
	}, [createAnalyticsEvent, onOpen, source]);

	const closeModal = useCallback(() => setIsModalOpen(false), []);

	return (
		<>
			{variant === 'footer' ? (
				<Pressable
					xcss={cx(styles.footerButton, shouldFitContainer && styles.fullWidth)}
					onClick={handleClick}
					testId={testId}
				>
					<ShareContextAgentIcons />
					<Text color="color.text.subtle" size="medium" weight="medium" maxLines={1}>
						{formatMessage(i18n.shareContextWithAgent)}
					</Text>
				</Pressable>
			) : (
				<Button
					appearance="primary"
					iconBefore={LinkExternalIcon}
					onClick={handleClick}
					shouldFitContainer={shouldFitContainer}
					testId={testId}
				>
					{formatMessage(i18n.shareContext)}
				</Button>
			)}
			{/* Keep the modal mounted so its close transition can finish. */}
			{hasOpenedModal && (
				<ShareContextModalLoadable
					isOpen={isModalOpen}
					onClose={closeModal}
					contentId={contentId}
					onAskRovo={onAskRovo}
				/>
			)}
		</>
	);
}
